import React from 'react';
import { ExternalLink, BookOpen } from 'lucide-react';
import Logo from '../components/Logo';

const DOCS_URL =
  'https://www.docs.shopiweb.fr/theme-premium/pour-commencer/installation';

const ObsoleteLanding: React.FC = () => {
  return (
    <div className="obsolete-landing relative min-h-screen overflow-hidden flex flex-col">
      <div className="obsolete-landing__glow" aria-hidden="true" />
      <div className="obsolete-landing__grid" aria-hidden="true" />

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-10 flex items-center gap-3 text-[#0f172a]">
          <Logo className="h-12 w-12 text-blue-600" />
          <span className="font-display text-3xl tracking-tight sm:text-4xl">
            Shopiweb Manager
          </span>
        </div>

        <p className="mb-4 inline-flex items-center rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-600">
          Méthode obsolète depuis janvier 2026
        </p>

        <h1 className="font-display max-w-2xl text-2xl leading-snug text-slate-900 sm:text-3xl">
          Suite aux mises à jour Shopify, l&apos;ancienne méthode n&apos;est plus
          supportée.
        </h1>

        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
          Passez à une installation simplifiée, sans API, en 1 clic. Suivez la
          documentation officielle Shopiweb pour migrer votre boutique.
        </p>

        <a
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-10 inline-flex items-center gap-2.5 rounded-lg bg-blue-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition duration-300 hover:bg-blue-700 hover:shadow-blue-600/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <BookOpen className="h-5 w-5" aria-hidden="true" />
          Voir la documentation d&apos;installation
          <ExternalLink
            className="h-4 w-4 opacity-80 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            aria-hidden="true"
          />
        </a>

        <p className="mt-6 text-sm text-slate-500">
          Besoin d&apos;aide ?{' '}
          <a
            href="mailto:support@shopiweb.fr"
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            support@shopiweb.fr
          </a>
        </p>
      </main>

      <footer className="relative z-10 pb-8 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Shopiweb
        <a
          href="/login"
          className="ml-2 text-slate-300/40 hover:text-slate-400 transition-colors"
          aria-label="Accès administrateur"
        >
          ·
        </a>
      </footer>

      <style>{`
        .obsolete-landing {
          background:
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(37, 99, 235, 0.14), transparent 55%),
            radial-gradient(ellipse 50% 40% at 100% 100%, rgba(14, 165, 233, 0.1), transparent 50%),
            linear-gradient(165deg, #f8fafc 0%, #eef2ff 45%, #f1f5f9 100%);
        }

        .obsolete-landing .font-display {
          font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
        }

        .obsolete-landing__glow {
          position: absolute;
          inset: 20% 10% auto;
          height: 40%;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.12), transparent 70%);
          filter: blur(40px);
          animation: obsolete-pulse 8s ease-in-out infinite;
          pointer-events: none;
        }

        .obsolete-landing__grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse 70% 60% at 50% 40%, black, transparent);
          pointer-events: none;
          animation: obsolete-drift 24s linear infinite;
        }

        @keyframes obsolete-pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }

        @keyframes obsolete-drift {
          from { background-position: 0 0; }
          to { background-position: 48px 48px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .obsolete-landing__glow,
          .obsolete-landing__grid {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ObsoleteLanding;
